import { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    Grid,
    InputAdornment,
    Chip,
    MenuItem,
    Checkbox,
    ListItemText,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Person,
    Email,
    Phone as PhoneIcon,
    Business,
    Message as MessageIcon,
    Settings,
    Tune,
    Speed,
    Layers,
    Groups,
} from '@mui/icons-material';

import { validateEmail, validatePhone } from '../../../../services/utils';
import { calculateFleetPriceBreakdown } from './clearBoxConfigUtils';
import Turnstile from '../Turnstile';
import { getSubmitErrorMessage } from '../../../../services/utils';

// Section definitions for grouping configuration
const sectionDefinitions = [
    { id: 'machine-info', title: 'Machine Information' },
    { id: 'scanner-config', title: 'Scanner Configuration' },
    { id: 'monitoring-config', title: 'Monitoring Configuration' },
];

function buildConfigurationEntries(questionnaire, answers) {
    if (!questionnaire || !answers) return { sections: [], other: [] };

    const steps = questionnaire.steps || [];
    const sections = {};
    const other = [];

    // Initialize section buckets
    sectionDefinitions.forEach((s) => {
        sections[s.id] = [];
    });

    steps.forEach((question) => {
        const answer = answers[question.id];

        if (
            answer === undefined ||
            answer === null ||
            answer === '' ||
            (Array.isArray(answer) && answer.length === 0)
        ) {
            return;
        }

        let formattedAnswer = '';

        if (question.type === 'single-choice' || question.type === 'single-select') {
            const option = (question.options || []).find((opt) => opt.id === answer);
            formattedAnswer = option ? option.label : String(answer);
        } else if (question.type === 'multi-choice' || question.type === 'multi-select') {
            const current = Array.isArray(answer) ? answer : [];
            const labels = current
                .map((id) => (question.options || []).find((opt) => opt.id === id))
                .filter(Boolean)
                .map((opt) => opt.label);
            formattedAnswer = labels.length ? labels.join(', ') : current.join(', ');
        } else {
            formattedAnswer = String(answer);
        }

        if (formattedAnswer.trim()) {
            const entry = { question, formattedAnswer };
            const sectionId = question.section;
            if (sectionId && sections[sectionId]) {
                sections[sectionId].push(entry);
            } else {
                other.push(entry);
            }
        }
    });

    // Convert to array with section info
    const sectionArray = sectionDefinitions
        .map((s) => ({ ...s, entries: sections[s.id] }))
        .filter((s) => s.entries.length > 0);

    return { sections: sectionArray, other };
}

function getQuestionIcon(question, index) {
    const hint = (question && question.id) || '';

    if (hint.includes('laser') || hint.includes('power')) return <Speed fontSize="small" />;
    if (hint.includes('scanner') || hint.includes('scan')) return <Tune fontSize="small" />;
    if (hint.includes('machine') || hint.includes('printer')) return <Settings fontSize="small" />;
    if (hint.includes('material') || hint.includes('alloy')) return <Layers fontSize="small" />;

    const icons = [
        <Settings fontSize="small" key="settings" />,
        <Tune fontSize="small" key="tune" />,
        <Speed fontSize="small" key="speed" />,
        <Layers fontSize="small" key="layers" />,
    ];

    return icons[index % icons.length];
}

function getShortLabel(question) {
    if (question && question.shortLabel) {
        return question.shortLabel;
    }

    const rawTitle = (question && question.title) ? question.title : '';
    let label = rawTitle.trim();

    const prefixes = [
        'What is your',
        'What is the',
        'What is',
        'What are your',
        'What are the',
        'What are',
        'Please specify',
        'Please select',
        'Select',
        'Choose',
    ];

    prefixes.forEach((prefix) => {
        if (label.toLowerCase().startsWith(prefix.toLowerCase())) {
            label = label.substring(prefix.length).trim();
        }
    });

    if (label.endsWith('?')) {
        label = label.slice(0, -1).trim();
    }

    const lower = label.toLowerCase();
    if (lower.includes('machine model') || lower.includes('printer model')) {
        return 'Model';
    }

    if (!label) {
        return 'Setting';
    }

    return label;
}

export default function RequestQuoteModal({ open, onClose, questionnaire, machines = [], answers, onAnswerChange }) {
    const [formValues, setFormValues] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        americaMakesMember: '',
        message: '',
    });

    // Pricing calculation — sum across all machines
    const basePrice = questionnaire?.basePrice ?? 9800;
    const additionalLaserPrice = questionnaire?.additionalLaserPrice ?? 4300;

    const allMachinesForPricing = machines.length > 0 ? machines : [{ answers: answers || {} }];
    const priceBreakdown = calculateFleetPriceBreakdown(allMachinesForPricing, basePrice, additionalLaserPrice);

    // Touched state to track if user has interacted with the form (for validation feedback)
    const [touched, setTouched] = useState({
        name: false,
        email: false,
        phone: false,
        company: false,
        americaMakesMember: false,
        message: false,
    });

    const [editingQuestionId, setEditingQuestionId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const turnstileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setFormValues({
                name: '',
                email: '',
                phone: '',
                company: '',
                americaMakesMember: '',
                message: '',
            });
            setEditingQuestionId(null);
            setSubmitError(null);
            setSubmitSuccess(false);
            setCaptchaToken('');
        }
    }, [open, questionnaire, answers]);

    const handleChange = (field) => (e) => {
        const value = e?.target?.value ?? e;
        setFormValues((prev) => ({ ...prev, [field]: value }));
    };

    // To mark field as touched on blur for validation feedback
    const handleBlur = (field) => () => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    // Validation logic
    const getFieldError = (field, label) => {
        if (!touched[field]) return '';
        if (!formValues[field] || (typeof formValues[field] === 'string' && !formValues[field].trim())) {
            return `${label} is required`;
        }
        return '';
    };

    const getEmailFieldError = () => {
        if (!touched.email) return '';
        if (!formValues.email.trim()) {
            return 'Email is required';
        }
        if (!validateEmail(formValues.email)){
            return 'Please enter a valid email address';
        }
        return '';
    }
    const getPhoneFieldError = () => {
        if (!touched.phone) return '';
        if (!formValues.phone.trim()) {
            return 'Phone is required';
        }
        if (!validatePhone(formValues.phone)){
            return 'Please enter a valid phone number';
        }
        return '';
    }
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formValues.name || !validateEmail(formValues.email) || !formValues.phone || !formValues.company || !formValues.message) {
            return;
        }
        if (!captchaToken) {
            setSubmitError('Please wait for the security check to finish, then try again.');
            return;
        }

        // Validate required questionnaire fields across all machines
        const steps = questionnaire?.steps || [];
        const allMachines = machines.length > 0 ? machines : [{ name: 'Machine 1', answers: answers || {} }];
        
        for (const machine of allMachines) {
            const missingRequired = steps.filter((q) => {
                if (!q.required) return false;
                const answer = machine.answers ? machine.answers[q.id] : undefined;
                if (answer === undefined || answer === null || answer === '') return true;
                if (Array.isArray(answer) && answer.length === 0) return true;
                return false;
            });
            if (missingRequired.length > 0) {
                setSubmitError(
                    `${machine.name}: Please complete the required fields: ${missingRequired.map((q) => q.title).join(', ')}`,
                );
                return;
            }
        }

        setSubmitting(true);
        setSubmitError(null);
        try {
            const { submitRfq } = await import('../../../../services/api');
            const configuration = { machines: allMachines.map((m) => ({ name: m.name, answers: m.answers })) };
            await submitRfq({
                ...formValues,
                configuration,
                estimatedPrice: priceBreakdown.total,
                priceBreakdown: {
                    basePrice,
                    additionalLasers: priceBreakdown.additionalLasers,
                    additionalLaserPrice,
                    additionalCost: priceBreakdown.additionalCost,
                    total: priceBreakdown.total,
                    machineCount: priceBreakdown.machineCount,
                    perMachine: priceBreakdown.perMachine,
                },
                turnstileToken: captchaToken,
            });
            setSubmitSuccess(true);
            setTimeout(() => {
                onClose && onClose();
            }, 1500);
        } catch (err) {
            console.error('RFQ submission failed:', err);
            setSubmitError(getSubmitErrorMessage(err));
        } finally {
            setSubmitting(false);
            setCaptchaToken('');
            turnstileRef.current?.reset();
        }
    };

    const isSubmitDisabled = !formValues.name || !validateEmail(formValues.email) || !formValues.phone || !formValues.company || !formValues.americaMakesMember || !formValues.message || !captchaToken || submitting;

    // Build configuration entries per machine
    const allMachines = machines.length > 0 ? machines : [{ name: 'Machine 1', answers: answers || {} }];
    const machineConfigs = allMachines.map((m) => ({
        name: m.name,
        ...buildConfigurationEntries(questionnaire, m.answers),
    }));
    const hasConfiguration = machineConfigs.some((mc) => mc.sections.length > 0 || mc.other.length > 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="request-quote-title"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: 'var(--card-shadow)',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f6f8fb 100%)',
                    py: 2.5,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pr: 1,
                    pb: 1,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    mb: 1.5,
                    px: 3,
                    flexShrink: 0,
                }}
            >
                <DialogTitle
                    id="request-quote-title"
                    sx={{
                        flex: 1,
                        fontWeight: 600,
                        pr: 0,
                        fontFamily: 'Avenir Next LT Pro, sans-serif',
                        fontSize: { xs: '1.15rem', sm: '1.35rem' },
                    }}
                >
                    Request a ClearBox Quote (RFQ)
                </DialogTitle>
                <IconButton onClick={onClose} edge="end" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ pt: 0, overflow: 'auto', flex: 1 }}>
                {submitSuccess && (
                    <Alert severity="success" sx={{ mx: 3, mt: 1.5, borderRadius: 2 }}>
                        RFQ submitted! We'll be in touch soon.
                    </Alert>
                )}
                {submitError && (
                    <Alert severity="error" sx={{ mx: 3, mt: 1.5, borderRadius: 2 }}>
                        {submitError}
                    </Alert>
                )}
                {/* Contact & configuration section */}
                <Paper
                    component="form"
                    onSubmit={handleSubmit}
                    elevation={0}
                    sx={{
                        m: 0,
                        p: { xs: 2.5, sm: 3 },
                        backgroundColor: 'transparent',
                    }}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    mb: 0.5,
                                    color: '#333',
                                }}
                            >
                                Your details
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#6C98AD' }}
                            >
                                We will use this information to follow up with a tailored ClearBox quote.
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Full name"
                                value={formValues.name}
                                onChange={handleChange('name')}
                                onBlur={handleBlur('name')}
                                error={!!getFieldError('name', 'Name')}
                                helperText={getFieldError('name', 'Name')}
                                required
                                fullWidth
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(108, 152, 173, 0.03)',
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                            borderColor: 'rgba(108, 152, 173, 0.2)',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(108, 152, 173, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#24272a',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#6C98AD',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person sx={{ color: '#6C98AD' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Email"
                                type="email"
                                value={formValues.email}
                                onChange={handleChange('email')}
                                onBlur={handleBlur('email')}
                                error={!!getEmailFieldError()}
                                helperText={getEmailFieldError()}
                                required
                                fullWidth
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(108, 152, 173, 0.03)',
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                            borderColor: 'rgba(108, 152, 173, 0.2)',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(108, 152, 173, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#24272a',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#6C98AD',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email sx={{ color: '#6C98AD' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Phone number"
                                value={formValues.phone}
                                onChange={handleChange('phone')}
                                onBlur={handleBlur('phone')}
                                error={!!getPhoneFieldError()}
                                helperText={getPhoneFieldError()}
                                required
                                fullWidth
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(108, 152, 173, 0.03)',
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                            borderColor: 'rgba(108, 152, 173, 0.2)',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(108, 152, 173, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#24272a',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#6C98AD',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PhoneIcon sx={{ color: '#6C98AD' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Company / Organization"
                                value={formValues.company}
                                onChange={handleChange('company')}
                                onBlur={handleBlur('company')}
                                error={!!getFieldError('company', 'Company')}
                                helperText={getFieldError('company', 'Company')}
                                required
                                fullWidth
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(108, 152, 173, 0.03)',
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                            borderColor: 'rgba(108, 152, 173, 0.2)',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(108, 152, 173, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#24272a',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#6C98AD',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Business sx={{ color: '#6C98AD' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                label="Are you an America Makes member?"
                                value={formValues.americaMakesMember}
                                onChange={handleChange('americaMakesMember')}
                                onBlur={handleBlur('americaMakesMember')}
                                error={!!getFieldError('americaMakesMember', 'This field')}
                                helperText={getFieldError('americaMakesMember', 'This field')}
                                required
                                fullWidth
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(108, 152, 173, 0.03)',
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                            borderColor: 'rgba(108, 152, 173, 0.2)',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(108, 152, 173, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        py: 1.5,
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#24272a',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#6C98AD',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Groups sx={{ color: '#6C98AD' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select an option</em>
                                </MenuItem>
                                <MenuItem value="yes">Yes</MenuItem>
                                <MenuItem value="no">No</MenuItem>
                                <MenuItem value="planning">Not yet, but planning to</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            {hasConfiguration && (
                                <Box
                                    sx={{
                                        mb: 1.5,
                                        p: 1.5,
                                        borderRadius: 2,
                                        backgroundColor: 'rgba(0,0,0,0.015)',
                                    }}
                                    aria-label="Selected ClearBox configuration settings"
                                >
                                    {machineConfigs.map((machineConfig, machineIdx) => {
                                        const { name: machineName, sections: configurationSections, other: otherEntries } = machineConfig;
                                        const hasMachineConfig = configurationSections.length > 0 || otherEntries.length > 0;
                                        if (!hasMachineConfig) return null;

                                        return (
                                            <Box key={machineIdx} sx={{ mb: machineIdx < machineConfigs.length - 1 ? 2.5 : 0 }}>
                                                {allMachines.length > 1 && (
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            color: '#333',
                                                            mb: 1,
                                                            pb: 0.5,
                                                            borderBottom: '1px solid rgba(108,152,173,0.2)',
                                                        }}
                                                    >
                                                        {machineName}
                                                    </Typography>
                                                )}
                                    {configurationSections.map((section) => (
                                        <Box key={section.id} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600,
                                                    color: 'var(--secondary-color, #6C98AD)',
                                                    letterSpacing: '0.5px',
                                                    display: 'block',
                                                    mb: 0.75,
                                                }}
                                            >
                                                {section.title}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.75,
                                                }}
                                                role="list"
                                            >
                                                {section.entries.map(({ question, formattedAnswer }, index) => {
                                                    const shortLabel = getShortLabel(question);
                                                    const isEditing = editingQuestionId === question.id;
                                                    const answer = answers ? answers[question.id] : undefined;

                                                    if (!onAnswerChange) {
                                                        return (
                                                            <Chip
                                                                key={question.id}
                                                                label={`${shortLabel}: ${formattedAnswer}`}
                                                                size="small"
                                                                icon={getQuestionIcon(question, index)}
                                                                sx={{
                                                                    borderRadius: '999px',
                                                                    fontSize: '0.75rem',
                                                                    maxWidth: 260,
                                                                    px: 1.5,
                                                                    height: 28,
                                                                    backgroundColor: 'rgba(108, 152, 173, 0.08)',
                                                                    color: '#1f2a33',
                                                                    '& .MuiChip-label': {
                                                                        whiteSpace: 'nowrap',
                                                                        textOverflow: 'ellipsis',
                                                                        overflow: 'hidden',
                                                                    },
                                                                }}
                                                                role="listitem"
                                                            />
                                                        );
                                                    }

                                                    if (isEditing) {
                                                        if (question.type === 'text') {
                                                            return (
                                                                <TextField
                                                                    key={question.id}
                                                                    label={shortLabel}
                                                                    value={typeof answer === 'string' ? answer : ''}
                                                                    onChange={(e) =>
                                                                        onAnswerChange(
                                                                            question.id,
                                                                            e.target.value,
                                                                        )
                                                                    }
                                                                    onBlur={() => setEditingQuestionId(null)}
                                                                    autoFocus
                                                                    size="small"
                                                                    sx={{
                                                                        minWidth: 160,
                                                                        maxWidth: 260,
                                                                    }}
                                                                    inputProps={{
                                                                        'aria-label': `${shortLabel} configuration`,
                                                                    }}
                                                                />
                                                            );
                                                        }

                                                        if (question.type === 'number') {
                                                            const numericValue =
                                                                answer === '' ||
                                                                answer === undefined ||
                                                                answer === null
                                                                    ? ''
                                                                    : answer;
                                                            return (
                                                                <TextField
                                                                    key={question.id}
                                                                    label={shortLabel}
                                                                    type="number"
                                                                    value={numericValue}
                                                                    onChange={(e) => {
                                                                        const raw = e.target.value;
                                                                        const numeric = raw === '' ? '' : Number(raw);
                                                                        onAnswerChange(
                                                                            question.id,
                                                                            Number.isNaN(numeric) ? '' : numeric,
                                                                        );
                                                                    }}
                                                                    onBlur={() => setEditingQuestionId(null)}
                                                                    autoFocus
                                                                    size="small"
                                                                    sx={{
                                                                        minWidth: 120,
                                                                        maxWidth: 220,
                                                                    }}
                                                                    inputProps={{
                                                                        'aria-label': `${shortLabel} configuration`,
                                                                        min: question.min ?? 0,
                                                                    }}
                                                                />
                                                            );
                                                        }

                                                        if (question.type === 'single-choice' || question.type === 'single-select') {
                                                            return (
                                                                <TextField
                                                                    key={question.id}
                                                                    select
                                                                    label={shortLabel}
                                                                    value={answer ?? ''}
                                                                    onChange={(e) => {
                                                                        onAnswerChange(question.id, e.target.value);
                                                                    }}
                                                                    onBlur={() => setEditingQuestionId(null)}
                                                                    autoFocus
                                                                    size="small"
                                                                    sx={{
                                                                        minWidth: 180,
                                                                        maxWidth: 260,
                                                                    }}
                                                                    inputProps={{
                                                                        'aria-label': `${shortLabel} configuration`,
                                                                    }}
                                                                >
                                                                    {(question.options || []).map((opt) => (
                                                                        <MenuItem key={opt.id} value={opt.id}>
                                                                            {opt.label}
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            );
                                                        }

                                                        if (question.type === 'multi-choice' || question.type === 'multi-select') {
                                                            const current = Array.isArray(answer) ? answer : [];
                                                            return (
                                                                <TextField
                                                                    key={question.id}
                                                                    select
                                                                    label={shortLabel}
                                                                    value={current}
                                                                    SelectProps={{
                                                                        multiple: true,
                                                                        renderValue: (selected) => {
                                                                            const selectedOptions = (question.options || []).filter(
                                                                                (opt) => selected.includes(opt.id),
                                                                            );
                                                                            return selectedOptions
                                                                                .map((opt) => opt.label)
                                                                                .join(', ');
                                                                        },
                                                                    }}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        const nextValue =
                                                                            typeof value === 'string'
                                                                                ? value.split(',')
                                                                                : value;
                                                                        onAnswerChange(question.id, nextValue);
                                                                    }}
                                                                    onBlur={() => setEditingQuestionId(null)}
                                                                    autoFocus
                                                                    size="small"
                                                                    sx={{
                                                                        minWidth: 200,
                                                                        maxWidth: 280,
                                                                    }}
                                                                    inputProps={{
                                                                        'aria-label': `${shortLabel} configuration`,
                                                                    }}
                                                                >
                                                                    {(question.options || []).map((opt) => (
                                                                        <MenuItem key={opt.id} value={opt.id}>
                                                                            <Checkbox
                                                                                checked={current.indexOf(opt.id) > -1}
                                                                                sx={{
                                                                                    p: 0.5,
                                                                                    mr: 1,
                                                                                }}
                                                                            />
                                                                            <ListItemText primary={opt.label} />
                                                                        </MenuItem>
                                                                    ))}
                                                                </TextField>
                                                            );
                                                        }

                                                        return null;
                                                    }

                                                    const label = `${shortLabel}: ${formattedAnswer}`;
                                                    return (
                                                        <Chip
                                                            key={question.id}
                                                            label={label}
                                                            size="small"
                                                            role="listitem"
                                                            onClick={() => setEditingQuestionId(question.id)}
                                                            tabIndex={0}
                                                            aria-label={`Edit ${shortLabel}. Current value: ${formattedAnswer}`}
                                                            onKeyDown={(event) => {
                                                                if (event.key === 'Enter' || event.key === ' ') {
                                                                    event.preventDefault();
                                                                    setEditingQuestionId(question.id);
                                                                }
                                                            }}
                                                            sx={{
                                                                borderRadius: '999px',
                                                                fontSize: '0.75rem',
                                                                maxWidth: 260,
                                                                px: 1.5,
                                                                height: 28,
                                                                backgroundColor: 'rgba(108, 152, 173, 0.04)',
                                                                color: '#1f2a33',
                                                                border: '1px solid rgba(108, 152, 173, 0.35)',
                                                                cursor: 'pointer',
                                                                transition:
                                                                    'background-color 0.18s ease-out, border-color 0.18s ease-out, box-shadow 0.18s ease-out, transform 0.08s ease-out',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(108, 152, 173, 0.14)',
                                                                    borderColor: 'rgba(108, 152, 173, 0.8)',
                                                                    boxShadow: '0 0 0 1px rgba(108, 152, 173, 0.25)',
                                                                },
                                                                '&:focus-visible': {
                                                                    outline: '2px solid #1a4f63',
                                                                    outlineOffset: 2,
                                                                },
                                                                '& .MuiChip-label': {
                                                                    whiteSpace: 'nowrap',
                                                                    textOverflow: 'ellipsis',
                                                                    overflow: 'hidden',
                                                                    fontWeight: 500,
                                                                    letterSpacing: 0.02,
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    ))}
                                    {/* Other entries without a section */}
                                    {otherEntries.length > 0 && (
                                        <Box sx={{ mt: configurationSections.length > 0 ? 2 : 0 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    fontWeight: 600,
                                                    color: 'var(--secondary-color, #6C98AD)',
                                                    letterSpacing: '0.5px',
                                                    display: 'block',
                                                    mb: 0.75,
                                                }}
                                            >
                                                Other
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 0.75,
                                                }}
                                                role="list"
                                            >
                                                {otherEntries.map(({ question, formattedAnswer }, index) => {
                                                    const shortLabel = getShortLabel(question);
                                                    return (
                                                        <Chip
                                                            key={question.id}
                                                            label={`${shortLabel}: ${formattedAnswer}`}
                                                            size="small"
                                                            icon={getQuestionIcon(question, index)}
                                                            sx={{
                                                                borderRadius: '999px',
                                                                fontSize: '0.75rem',
                                                                maxWidth: 260,
                                                                px: 1.5,
                                                                height: 28,
                                                                backgroundColor: 'rgba(108, 152, 173, 0.08)',
                                                                color: '#1f2a33',
                                                                '& .MuiChip-label': {
                                                                    whiteSpace: 'nowrap',
                                                                    textOverflow: 'ellipsis',
                                                                    overflow: 'hidden',
                                                                },
                                                            }}
                                                            role="listitem"
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                                        );
                                    })}
                                </Box>
                            )}
                            <TextField
                                label="Please provide details of your inquiry for a quote."
                                value={formValues.message}
                                onChange={handleChange('message')}
                                onBlur={handleBlur('message')}
                                error={!!getFieldError('message', 'Message')}
                                helperText={getFieldError('message', 'Message')}
                                required
                                fullWidth
                                multiline
                                minRows={3}
                                size="medium"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: 'rgba(108, 152, 173, 0.03)',
                                        transition: 'all 0.3s ease',
                                        '& fieldset': {
                                            borderColor: 'rgba(108, 152, 173, 0.2)',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#6C98AD',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(108, 152, 173, 0.06)',
                                        },
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: '0.95rem',
                                        py: 1.5,
                                    },
                                    '& .MuiInputBase-inputMultiline': {
                                        fontSize: '0.95rem',
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: '#24272a',
                                        fontWeight: 500,
                                        '&.Mui-focused': {
                                            color: '#6C98AD',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment
                                            position="start"
                                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                                        >
                                            <MessageIcon sx={{ color: '#6C98AD' }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pt: 2,
                    pb: 2,
                    mt: 0.5,
                    borderTop: '1px solid rgba(0,0,0,0.06)',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                    <Turnstile
                        ref={turnstileRef}
                        onVerify={setCaptchaToken}
                        onExpire={() => setCaptchaToken('')}
                        onError={() => setSubmitError('The verification could not load. Please refresh the page or try again later.')}
                    />
                </Box>
                <Button
                    onClick={onClose}
                    color="inherit"
                    size="medium"
                    sx={{
                        textTransform: 'none',
                        px: 2,
                        fontWeight: 500,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                    size="medium"
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                    sx={{
                        textTransform: 'none',
                        px: 3,
                        py: 0.9,
                        borderRadius: '999px',
                        fontWeight: 600,
                        boxShadow: 'var(--card-shadow)',
                        backgroundColor: '#6C98AD',
                        '&:hover': {
                            backgroundColor: '#5a8499',
                            boxShadow: 'var(--card-shadow-hover)',
                        },
                    }}
                >
                    {submitting ? 'Submitting...' : 'Request Quote'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
